import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Check, X, ImageIcon, Video } from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Checkbox from "../common/Checkbox";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import Pagination from "../common/Pagination";
import { getPendingChatMessages } from "../../services/chatMediaApprovalService";
import { useChat } from "../../context/ChatContext";
import { color, radius } from "../../styles/theme";

const PAGE_SIZE = 10;

/**
 * Moderation queue for one event's chat: every "image"/"video" message
 * ChatHub.SendMessage held instead of broadcasting (ChatRoom.
 * RequireMediaApproval is on and the sender isn't yet trusted in this room).
 * Opened from ChatThreadPage's header, admin-only — same gating as the
 * existing Settings gear (permissions.isAdmin / canManageSingleMedia,
 * matching ChatMediaApprovalService's own backend permission check).
 */
const PendingApprovalModal = ({ eventId, onClose, onReviewed }) => {
  const { t } = useTranslation("chat");
  const chat = useChat();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [trustChoice, setTrustChoice] = useState({}); // { [messageId]: boolean }
  const [rejectTarget, setRejectTarget] = useState(null); // messageId currently showing its reason box
  const [rejectReason, setRejectReason] = useState("");

  const load = (pageNumber) => {
    setLoading(true);
    return getPendingChatMessages(eventId, pageNumber, PAGE_SIZE)
      .then((res) => {
        const result = res.data?.Result || {};
        setItems(result.Items || []);
        setTotal(result.Total || 0);
      })
      .catch(() => toast.error(t("pendingApproval.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const afterAction = () => {
    onReviewed?.();
    // A page can go empty after its last item is handled — step back one
    // rather than showing a dead empty page while later pages still have items.
    if (items.length === 1 && page > 1) setPage((p) => p - 1);
    else load(page);
  };

  // Routed through ChatContext's approveMessage/rejectMessage (not the raw
  // service calls directly) so acting from this modal also cleans up/updates
  // any matching inline blurred preview MessageBubble is showing for the same
  // item — otherwise a reject here would leave that preview stuck, since
  // ChatMessageRejected only ever reaches the sender's own connection, not
  // this moderator's.
  //
  // ChatMediaApprovalService's approve/reject return a plain SuccessResponse
  // (Message/Result, no Success flag) — every real failure (not-pending,
  // not-found, forbidden) already comes back as a non-2xx status via
  // ToActionResult, so axios throws into the catch below; a resolved promise
  // here is always a genuine success.
  const handleApprove = async (item) => {
    setBusyId(item.Id);
    try {
      const res = await chat.approveMessage(item.Id, item.MessageId, !!trustChoice[item.Id]);
      toast.success(res.data?.Message || t("pendingApproval.approveSuccess"));
      afterAction();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("pendingApproval.approveError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (item) => {
    setBusyId(item.Id);
    try {
      const res = await chat.rejectMessage(item.Id, item.MessageId, rejectReason.trim(), !!trustChoice[item.Id]);
      toast.success(res.data?.Message || t("pendingApproval.rejectSuccess"));
      setRejectTarget(null);
      setRejectReason("");
      afterAction();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("pendingApproval.rejectError"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal
      title={t("pendingApproval.title")}
      subtitle={t("pendingApproval.subtitle")}
      onClose={onClose}
      busy={busyId != null}
      maxWidth={560}
      footer={<Button variant="secondary" onClick={onClose} disabled={busyId != null}>{t("settings.close")}</Button>}
    >
      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState text={t("pendingApproval.empty")} />
      ) : (
        <>
          <div style={s.list}>
            {items.map((item) => {
              const busy = busyId === item.Id;
              const isImage = item.MessageType === "image";
              const isVideo = item.MessageType === "video";
              return (
                <div key={item.Id} style={s.row}>
                  <div style={s.preview}>
                    {isImage ? (
                      <a href={item.Message} target="_blank" rel="noreferrer">
                        <img src={item.Message} alt="" style={s.thumb} />
                      </a>
                    ) : isVideo ? (
                      <a href={item.Message} target="_blank" rel="noreferrer" style={s.mediaIconBox}>
                        <Video size={20} strokeWidth={2} color={color.gray400} />
                      </a>
                    ) : (
                      <span style={s.mediaIconBox}><ImageIcon size={20} strokeWidth={2} color={color.gray400} /></span>
                    )}
                  </div>

                  <div style={s.info}>
                    <span style={s.senderName}>{item.SenderName || t("pendingApproval.unknownSender")}</span>
                    <span style={s.meta}>
                      {new Date(item.Timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <label style={s.trustRow}>
                      <Checkbox
                        checked={!!trustChoice[item.Id]}
                        onChange={(e) => setTrustChoice((prev) => ({ ...prev, [item.Id]: e.target.checked }))}
                        aria-label={t("pendingApproval.trustFuture")}
                      />
                      <span>{t("pendingApproval.trustFuture")}</span>
                    </label>

                    {rejectTarget === item.Id && (
                      <div style={s.reasonBox}>
                        <textarea
                          className="input"
                          rows={2}
                          maxLength={1000}
                          placeholder={t("pendingApproval.reasonPlaceholder")}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>

                  <div style={s.actions}>
                    {rejectTarget === item.Id ? (
                      <>
                        <Button variant="danger" size="sm" disabled={busy} onClick={() => handleReject(item)}>
                          {t("pendingApproval.confirmReject")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => { setRejectTarget(null); setRejectReason(""); }}
                        >
                          {t("composer.cancelEdit")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="primary" size="sm" icon={Check} disabled={busy} onClick={() => handleApprove(item)}>
                          {t("pendingApproval.approve")}
                        </Button>
                        <Button
                          variant="danger-ghost"
                          size="sm"
                          icon={X}
                          disabled={busy}
                          onClick={() => { setRejectTarget(item.Id); setRejectReason(""); }}
                        >
                          {t("pendingApproval.reject")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} totalCount={total} onPageChange={setPage} disabled={loading} />
        </>
      )}
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 10 },
  row: {
    display: "flex", gap: 12, padding: 12, borderRadius: radius.sm,
    border: `1px solid ${color.border}`, alignItems: "flex-start", flexWrap: "wrap",
  },
  preview: { flexShrink: 0 },
  thumb: { width: 56, height: 56, borderRadius: radius.sm, objectFit: "cover", display: "block" },
  mediaIconBox: {
    width: 56, height: 56, borderRadius: radius.sm, background: color.gray100,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  info: { flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 6 },
  senderName: { fontSize: 13, fontWeight: 700, color: color.textPrimary },
  meta: { fontSize: 11.5, color: color.textMuted },
  trustRow: { display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: color.gray700, cursor: "pointer" },
  reasonBox: { marginTop: 2 },
  actions: { display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" },
};

export default PendingApprovalModal;
